'use client';

import Image from 'next/image';
import { useState } from 'react';
import Xarrow from 'react-xarrows';
import RequestPilotModal from '@/components/RequestPilotModal';

// ─── Nav ────────────────────────────────────────────────────────────────────

function Nav({ onRequestPilot }: { onRequestPilot: () => void }) {
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <nav className="sticky top-0 z-50 flex items-center justify-between px-16 py-3 bg-white border-b border-gray-200 shadow-sm">
      <span className="font-bold tracking-wider text-gray-900" style={{ fontFamily: 'Inter, sans-serif', fontSize: '20px' }}>Deliberate</span>
      <div className="hidden md:flex items-center gap-6" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#080615' }}>
        <a href="#product" onClick={(e) => scrollToSection(e, 'product')} className="hover:text-gray-900 cursor-pointer">Product</a>
        <a href="#problems" onClick={(e) => scrollToSection(e, 'problems')} className="hover:text-gray-900 cursor-pointer">Problems</a>
        <a href="#how-it-works" onClick={(e) => scrollToSection(e, 'how-it-works')} className="hover:text-gray-900 cursor-pointer">How it works</a>
        <a href="#why-deliberate" onClick={(e) => scrollToSection(e, 'why-deliberate')} className="hover:text-gray-900 cursor-pointer">Why Deliberate</a>
      </div>
      <button 
        onClick={onRequestPilot}
        className="rounded-full bg-gray-900 px-4 py-1.5 font-medium text-white hover:bg-gray-800 cursor-pointer"
        style={{ fontSize: '14px', fontFamily: 'Inter, sans-serif' }}
      >
        Request a Pilot
      </button>
    </nav>
  );
}

// ─── Hero ────────────────────────────────────────────────────────────────────

function Hero({ onRequestPilot }: { onRequestPilot: () => void }) {
  return (
    <section id="product" className="grid grid-cols-1 xl:grid-cols-2 min-h-[500px] scroll-mt-16 py-18" style={{ backgroundColor: '#F8FAFC' }}>
      <div className="mx-auto xl:mx-0 xl:contents" style={{ maxWidth: '846px', width: '100%' }}>
        {/* Left — dark panel */}
        <div className="flex flex-col justify-center py-12 xl:mx-auto px-4 xl:p-0">
          <div className="xl:pl-[40px]">
            <span className="inline-block mb-3 text-[10px] font-medium tracking-wide uppercase rounded px-2 py-0.5 w-fit" style={{ color: '#2563EB', backgroundColor: '#ECF0FF' }}>
              THE SYSTEM OF RECORD
            </span>
            <h1 className="text-gray-900 mb-3" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '52px', lineHeight: '60px', maxWidth: '600px', minHeight: '180px' }}>
              The system of record<br />for organizational<br />decisions.
            </h1>
            <p className="text-gray-600 text-xs leading-relaxed mb-6" style={{fontSize: '18px', fontFamily: 'Inter, sans-serif', maxWidth: '600px'}}>
              Deliberate helps organizations capture important decisions, preserve their judgment behind them before outcomes are known, and review them over time, building organizational clarity, accountability, and long-term learning.        
            </p>
            <button 
              onClick={onRequestPilot}
              className="rounded-full w-fit rounded bg-gray-900 px-16 py-4 text-xs font-medium text-white hover:bg-gray-800 cursor-pointer" 
              style={{fontSize: '14px', fontFamily: 'Inter, sans-serif',}}
            >
              Request a Pilot
            </button>
          </div>
        </div>

        {/* Right — app mockup placeholder */}
        <div className="flex items-center justify-center xl:pr-0 xl:overflow-hidden">
          <div 
            className="xl:border-[1.9px] xl:mr-[-22%]"
            style={{ 
              backgroundColor: '#F5F5F5', 
              borderColor: '#E9E9E9',
              borderRadius: '19px',
              padding: '0'
            }}
          >
            <div className="xl:py-[27px] xl:pr-0 xl:pl-[40px] p-4">
              <img 
                src="/hero_image2.png" 
                alt="Deliberate Decision Ledger" 
                className="w-full max-w-4xl mx-auto xl:min-h-[516px]"
                style={{ 
                  borderRadius: '9px',
                  backfaceVisibility: 'hidden',
                  transform: 'translateZ(0)',
                  height: 'auto',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── What Organizations Forget ───────────────────────────────────────────────

const forgetItems = [
  'Why it was made.',
  'What assumptions were believed.',
  'Which alternatives were considered.',
  'What tradeoffs were accepted.',
  'Whether the decision delivered the expected outcome.',
];

function WhatOrgsForget() {
  return (
    <section id="problems" className="py-16 px-8 text-center scroll-mt-16" style={{ backgroundColor: '#FFF' }}>
      <h2 className="font-bold text-gray-900 mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '32px' }}>
        What Organizations Forget
      </h2>
      <p className="text-gray-600 mb-1" style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px' }}>
        Organizations rarely lose the outcome of a decision.
      </p>
      <p className="text-gray-600 mb-8" style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px' }}>
        They lose everything before it.
      </p>

      <div className="mx-auto max-w-lg space-y-2 text-left mb-8" style={{ maxWidth: '682px' }}>
        {forgetItems.map((item, i) => (
          <div
            key={i}
            className="rounded px-3.5 py-2.5 text-gray-900 font-semibold mb-6"
            style={{ fontFamily: 'Inter, sans-serif', fontSize: '20px', padding: '20px 33px', borderRadius: '12px', backgroundColor: '#E3EAFF' }}
          >
            {item}
          </div>
        ))}
      </div>

      <p className="max-w-xl mx-auto leading-relaxed" style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px', color: '#4B5563', maxWidth: '800px' }}>
        As people leave, priorities change, and time passes, organizations revisit the same decisions because the original judgment has disappeared.
      </p>
      <p className="max-w-xl mx-auto leading-relaxed mt-6" style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px', color: '#4B5563', maxWidth: '800px' }}>
        Deliberate preserves that judgment before it is lost.
      </p>
    </section>
  );
}

// ─── When Judgment Isn't Preserved ──────────────────────────────────────────

const quotes = [
  '"Didn\'t we already decide this?"',
  '"Why did we make this decision?"',
  '"Who owns this decision?"',
  '"Is this decision still in effect?"',
  '"Why are we discussing this again?"',
];

function JudgmentNotPreserved() {
  return (
    <section className="py-16 px-8" style={{ backgroundColor: '#F8FAFC' }}>
      <div className="max-w-5xl mx-auto" style={{maxWidth: '824px'}}>
        <h2 className="text-gray-900 mb-8 text-center" style={{ fontFamily: 'Inter, sans-serif', fontSize: '32px', fontWeight: 700 }}>
          When Organizational Judgment Isn't Preserved
        </h2>
        <div className="space-y-2.5 max-w-xl mx-auto" style={{maxWidth: '518px'}}>
          {quotes.map((q, i) => (
            <div
              key={i}
              className="border-l-3 pl-3 text-gray-700"
              style={{ maxWidth: '518px',borderLeftWidth: '6px',fontFamily: 'Inter, sans-serif', fontSize: '22px', fontWeight: 700, borderLeftColor: '#2563EB', paddingTop: '10px', paddingBottom: '10px', color: '#1F2937 !important' }}
            >
              {q}
            </div>
          ))}
        </div>
      </div>
      <p  style = {{maxWidth: '842px',fontSize: '18px', fontFamily: "Inter, sans-serif", color: '#4B5563'}} className="text-center text-gray-400 text-xs mt-10 max-w-xl mx-auto">
        These conversations aren't the problem. They're symptoms of judgment that was never preserved.
      </p>
    </section>
  );
}

// ─── How It Works ────────────────────────────────────────────────────────────

const steps = [
  {
    iconSrc: '/heroicons_camera.png',
    imageSrc: '/capture.png',
    title: 'Capture',
    description: 'Capture the original judgment before implementation begins.'
  },
  {
    iconSrc: '/heroicons_commit.png',
    imageSrc: '/commit.png',
    title: 'Commit',
    description: 'Preserve the decision exactly as it existed before the outcome was known.'
  },
  {
    iconSrc: '/heroicons_review.png',
    imageSrc: '/review.png',
    title: 'Review',
    description: 'Compare original expectations against real-world outcomes.'
  },
];

function HowItWorks() {
  return (
    <section id="how-it-works" className="py-16 px-8 scroll-mt-16" style={{ backgroundColor: '#FFF' }}>
      <div className="max-w-6xl mx-auto" style={{maxWidth: '1280px'}}>
        <h2 className="font-bold text-gray-900 text-center mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '32px' }}>
          How It Works
        </h2>
        <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px', maxWidth: '534px' }}>
          A complete lifecycle for capturing judgment, preserving it over time, and turning it into organizational learning.
        </p>

        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-4">
          {steps.map((step, i) => (
            <>
              <div key={i} className="bg-white rounded-lg flex flex-col justify-between w-full md:flex-1 md:max-w-sm" style={{maxWidth: '394px', fontFamily: 'Inter, sans-serif'}}>
                {/* Icon and Title on same line */}
                <div className="flex items-start gap-3 mb-3">
                  <Image
                    src={step.iconSrc}
                    alt={`${step.title} icon`}
                    width={32}
                    height={32}
                    className="w-8 h-8"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900" style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px' }}>
                      {step.title}
                    </h3>
                                      
                    {/* Description */}
                    <p className="text-gray-600 leading-relaxed flex-grow" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>
                      {step.description}
                    </p>
                  </div>
                </div>

                
                {/* Step Image */}
                <img 
                  src={step.imageSrc}
                  alt={`${step.title} screenshot`}
                  className="w-full h-auto"
                  style={{ 
                    display: 'block',
                    WebkitFontSmoothing: 'antialiased',
                    width: '394px',
                    boxShadow: '0 4px 4px 0 rgba(149, 164, 175, 0.15), 0 -4px 4px 0 rgba(149, 164, 175, 0.15)',
                    borderRadius: '12px'
                  }}
                />
              </div>
              
              {/* Arrow between cards (not after the last one) */}
              {i < steps.length - 1 && (
                <div className="flex-shrink-0 flex justify-center items-center">
                  {/* Down arrow on mobile */}
                  <svg className="w-6 h-6 text-blue-600 md:hidden" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" />
                  </svg>
                  {/* Right arrow on desktop */}
                  <svg className="w-6 h-6 text-blue-600 hidden md:block" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </div>
              )}
            </>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Organizational Memory ────────────────────────────────────────────────────

const decisions = [
  { id: '01', title: 'Q2 Pricing Change', date: 'Jan 15' },
  { id: '02', title: 'Expand into EU Market', date: 'Feb 3' },
  { id: '03', title: 'Vendor Security Assessment', date: 'Mar 12' },
  { id: '04', title: 'Data Rentention Policy', date: 'Apr 8' },
  { id: '05', title: 'Build vs. Buy Platform', date: 'May 22' },
];

function OrgMemory() {
  return (
    <section className="py-16 px-8" style={{ backgroundColor: '#F8FAFC' }}>
      <div className="max-w-5xl mx-auto" style={{maxWidth: '1280px'}}>
        <h2 className="font-bold text-gray-900 text-center mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '32px' }}>
          Every Reviewed Decision Builds Organizational Memory
        </h2>
        <p className="text-gray-600 text-center mb-12" style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px' }}>
          Over time, reviewed decisions become Organizational Learning.
        </p>

        <div className="flex flex-col md:flex-row gap-8 items-center mx-[5%] relative mx-auto" style={{maxWidth: '920px'}}>
          {/* Decision list - 60% width */}
          <div className="w-full md:w-[60%] relative" style={{maxWidth: '546px'}}>
            {/* Wrapper container for mobile - with ID for connector */}
            <div 
              id="decisions-container" 
              className="space-y-3 md:border-0 md:p-0 border-2 border-gray-200 rounded-lg p-4"
            >
              {decisions.map((d, i) => (
                <div 
                  key={i} 
                  id={`decision-${i}`}
                  className="flex items-center gap-4 px-4 py-6 bg-white border border-gray-200 rounded shadow-sm relative w-full md:max-w-[458px]"
                >
                  {/* Underlined ID number */}
                  <span className="text-gray-400 font-medium border-b border-gray-300 pb-0.5 w-6" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                    {d.id}
                  </span>
                  
                  {/* Content */}
                  <span className="text-gray-900 font-medium flex-1" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>
                    {d.title}
                  </span>
                  
                  {/* Reviewed badge with date */}
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="font-semibold px-2 py-0.5 rounded bg-green-100 text-green-700" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                      Reviewed
                    </span>
                    <span className="text-gray-500" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>{d.date}</span>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Arrow Connectors - only visible on desktop */}
            <div className="hidden md:block">
              {/* Decision 1 (index 0) to Decision 4 (index 3) */}
              <Xarrow
                start="decision-0"
                end="brain-box"
                color="#2563EB"
                strokeWidth={2}
                curveness={0.8}
                dashness={true}
                path="smooth"
              />
              
              {/* Decision 2 (index 1) to Decision 3 (index 2) */}
              <Xarrow
                start="decision-1"
                end="brain-box"
                color="#2563EB"
                strokeWidth={2}
                curveness={0.8}
                dashness={true}
                path="smooth"
              />
              
              {/* Decision 3 (middle, index 2) to Brain Box */}
              <Xarrow
                start="decision-2"
                end="brain-box"
                color="#2563EB"
                strokeWidth={2}
                path="straight"
                startAnchor={{ position: "right", offset: { y: 5.5 } }}
                endAnchor={{ position: "left", offset: { y: 0 } }}
                dashness={true}
              />
              <Xarrow
                start="decision-3"
                end="brain-box"
                color="#2563EB"
                strokeWidth={2}
                curveness={0.8}
                path="smooth"
                startAnchor="right"
                endAnchor="left"
                dashness={true}

              />
              <Xarrow
                start="decision-4"
                end="brain-box"
                color="#2563EB"
                strokeWidth={2}
                curveness={0.8}
                path="smooth"
                startAnchor="right"
                endAnchor="left"
                dashness={true}
              />
            </div>

            {/* Mobile connector - from container to brain box */}
            <div className="md:hidden">
              <Xarrow
                start="decisions-container"
                end="brain-box"
                color="#2563EB"
                strokeWidth={2}
                path="straight"
                startAnchor="bottom"
                endAnchor="top"
                dashness={true}
              />
            </div>
          </div>

          {/* Organizational Learning graphic - 40% width */}
          <div id="brain-box" className="flex flex-col items-center w-full md:w-[40%]">
            <div className="bg-blue-50 rounded-lg p-8 flex flex-col items-center gap-3 w-full max-w-xs border-2 border-blue-200">
              {/* Brain/Learning icon placeholder */}
              <div className="justify-center">
                <Image
                  src="/brain.png"
                  alt="Brain icon"
                  width={120}
                  height={120}
                />
              </div>
              <p className="text-gray-900 text-center" style={{ fontFamily: 'Helvetica', fontSize: '24px', fontWeight: 700 }}>Organizational</p>
              <p className="text-gray-900 text-center -mt-3" style={{ fontFamily: 'Helvetica', fontSize: '24px', fontWeight: 700 }}>Learning</p>
              {/* Line break image */}
              <Image
                src="/linebreak-brain.png"
                alt="Line break"
                width={270}
                height={20}
                className="mx-auto my-4"
              />
              <div className="flex flex-col gap-2 mt-2 items-start">
                <div className="flex items-start gap-2 text-gray-700">
                  <Image src="/tick-circle.png" alt="Check" width={22} height={22} className="flex-shrink-0 mt-0.5" />
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px' }}>Better decisions</span>
                </div>
                <div className="flex items-start gap-2 text-gray-700">
                  <Image src="/tick-circle.png" alt="Check" width={22} height={22} className="flex-shrink-0 mt-0.5" />
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px' }}>Faster alignment</span>
                </div>
                <div className="flex items-start gap-2 text-gray-700">
                  <Image src="/tick-circle.png" alt="Check" width={22} height={22} className="flex-shrink-0 mt-0.5" />
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px' }}>Stronger accountability</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Designed Around Org Judgment ────────────────────────────────────────────

const pillars = [
  {
    icon: <img src="/preserve_judgement.png" alt="Preserve Judgment" className="w-12 h-12" />,
    title: 'Preserve Judgment',
    desc: 'Capture the reasoning behind decisions before outcomes are known.',
  },
  {
    icon: <img src="/immutable_records.png" alt="Immutable Records" className="w-12 h-12" />,
    title: 'Immutable Records',
    desc: 'Every committed decision becomes an unchangeable organizational record.',
  },
  {
    icon: <img src="/exp_vs_reality.png" alt="Expectations vs Reality" className="w-12 h-12" />,
    title: 'Expectations vs Reality',
    desc: 'Every review compares original expectations against real-world outcomes.',
  },
  {
    icon: <img src="/historical_context.png" alt="Historical Context" className="w-12 h-12" />,
    title: 'Historical Context',
    desc: 'Ownership, attribution, and context are always preserved.',
  },
  {
    icon: <img src="/organizational_learning.png" alt="Organizational Learning" className="w-12 h-12" />,
    title: 'Organizational Learning',
    desc: 'Turn completed reviews into insights that improve future decisions.',
  },
];

function DesignedAround() {
  return (
    <section id="why-deliberate" className="py-16 px-8 scroll-mt-16" style={{ backgroundColor: '#F8FAFC' }}>
      <div>
        <div className='max-w-5xl mx-auto'>
          <h2 className="font-bold text-gray-900 text-center mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '32px' }}>
            Designed Around Organizational Judgment
          </h2>
          <p className="text-gray-600 text-center mb-12 w-[75%] mx-auto" style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px', color: '#4B5563' }}>
            Deliberate is intentionally designed to preserve organizational judgment, compare expectations against reality, and build organizational learning over time.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
          {pillars.map((p, i) => (
            <div key={i} className="flex flex-col gap-3 items-center text-center bg-white rounded-lg p-6 shadow-sm" style={{minWidth: '170px'}}>
              <div className="w-12 h-12 flex items-center justify-center">
                {p.icon}
              </div>
              <h3 className="font-semibold text-gray-900" style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px' }}>{p.title}</h3>
              <p className="text-gray-600 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif', fontSize: '18px' }}>{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CTA Footer ──────────────────────────────────────────────────────────────

function CtaFooter({ onRequestPilot }: { onRequestPilot: () => void }) {
  return (
    <section className="py-16 px-8" style={{ backgroundColor: '#F8FAFC' }}>
      <div className="py-16 px-8 text-center" style={{fontFamily: 'Inter, sans-serif', backgroundColor: '#0F172A', borderRadius: '24px'}}>
        <h2 className="text-xl md:text-2xl font-bold mb-3" style={{fontSize: '32px', fontFamily: 'Inter, sans-serif', color: '#FFF'}}>
          Pilot Deliberate with your leadership team.
        </h2>
        <p className="text-xs leading-relaxed max-w-md mx-auto mb-2" style={{fontSize: '18px', fontFamily: 'Inter, sans-serif', maxWidth: '518px', color: '#FFF'}}>
          We're partnering with organizations that want to preserve organizational judgment, compare expectations against reality, and build organizational learning over time.
        </p>
        <p className="text-xs my-6 mx-auto" style={{fontSize: '18px', fontFamily: 'Inter, sans-serif', maxWidth: '518px', color: '#FFF'}}>
          If you're interested in evaluating Deliberate within your organization, we'd love to talk.
        </p>
        <button 
          onClick={onRequestPilot}
          className="rounded-full bg-white px-24 py-4 text-xs font-medium text-gray-900 hover:bg-gray-100 cursor-pointer"
          style={{fontSize: '14px', fontFamily: 'Inter, sans-serif'}}
          >
          Request a Pilot
        </button>
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function HomePage() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleRequestPilot = () => {
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen font-sans">
      <style jsx global>{`
        html {
          scroll-behavior: smooth;
        }
      `}</style>
      <Nav onRequestPilot={handleRequestPilot} />
      <Hero onRequestPilot={handleRequestPilot} />
      <WhatOrgsForget />
      <JudgmentNotPreserved />
      <HowItWorks />
      <OrgMemory />
      <DesignedAround />
      <CtaFooter onRequestPilot={handleRequestPilot} />
      
      <RequestPilotModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
}
